import { z } from 'zod';

/**
 * Période disponible dans les sélecteurs du dashboard staff Product Usage.
 * Doit rester aligné avec PeriodSelector côté admin-dashboard.
 */
export const ProductUsagePeriodSchema = z.enum([
  'mtd',              // depuis le 1er du mois courant
  '30d',              // 30 derniers jours
  'previous_month',   // mois précédent (1er → dernier jour)
  '90d',              // 90 derniers jours
  'all_time',         // pas de borne basse
]);
export type ProductUsagePeriod = z.infer<typeof ProductUsagePeriodSchema>;

/**
 * KPIs globaux affichés en haut de la page /admin/product-usage.
 */
export const ProductUsageKpisSchema = z.object({
  actesGenerated:        z.number().int().nonnegative(),
  totalCases:            z.number().int().nonnegative(),
  casesWithSummary:      z.number().int().nonnegative(),
  caseDiscoMessages:     z.number().int().nonnegative(),
  caseDiscoConversations:z.number().int().nonnegative(),
  totalLlmCostUsd:       z.number().nonnegative(),
  activeUsersCount:      z.number().int().nonnegative(),
});
export type ProductUsageKpis = z.infer<typeof ProductUsageKpisSchema>;

/**
 * Un flux du Sankey "spécialisation × type d'acte".
 * `specialisation` et `actType` sont déjà normalisés LOWER+TRIM côté SQL.
 * "autres" est utilisé pour les valeurs hors top.
 */
export const SankeyFlowSchema = z.object({
  specialisation: z.string().min(1),
  actType:        z.string().min(1),
  count:          z.number().int().positive(),
});
export type SankeyFlow = z.infer<typeof SankeyFlowSchema>;

/**
 * Un bucket d'histogramme avec son label affichable et son count.
 * `order` permet de garder les buckets triés côté front sans redériver depuis le label.
 * Schéma générique réutilisé pour la distribution taille dossier, messages/conv, users/activité.
 */
export const HistogramBucketSchema = z.object({
  bucket: z.string().min(1),
  count:  z.number().int().nonnegative(),
  order:  z.number().int().nonnegative(),
});
export type HistogramBucket = z.infer<typeof HistogramBucketSchema>;

/**
 * Granularité de l'agrégation temporelle pour les séries chronologiques.
 * Choisie côté backend en fonction de la longueur de la période.
 */
export const TimeGranularitySchema = z.enum(['day', 'week', 'month']);
export type TimeGranularity = z.infer<typeof TimeGranularitySchema>;

/**
 * Un total par type de job sur la période.
 */
export const JobTypeCountSchema = z.object({
  jobType: z.string().min(1),
  count:   z.number().int().nonnegative(),
});
export type JobTypeCount = z.infer<typeof JobTypeCountSchema>;

/**
 * Un point de la série chronologique (1 par bucket × type de job).
 * `bucket` est une date ISO (YYYY-MM-DD) tronquée à la granularité.
 */
export const JobTimeSeriesPointSchema = z.object({
  bucket:  z.string(),
  jobType: z.string().min(1),
  count:   z.number().int().nonnegative(),
});
export type JobTimeSeriesPoint = z.infer<typeof JobTimeSeriesPointSchema>;

/**
 * Réponse de GET /api/admin/stats/jobs-activity
 * - byType : top 5 types + 'autres' (somme des autres) sur la période
 * - timeSeries : un point par bucket × type — sans remplissage 0
 *   (Nivo gère les séries avec des dates manquantes côté front)
 * - granularity : choisie côté backend selon la longueur de la période
 */
export const JobsActivityResponseSchema = z.object({
  byType:      z.array(JobTypeCountSchema),
  timeSeries:  z.array(JobTimeSeriesPointSchema),
  granularity: TimeGranularitySchema,
});
export type JobsActivityResponse = z.infer<typeof JobsActivityResponseSchema>;

/**
 * Une ligne de la table "Distribution cabinets / spécialisation dominante".
 */
export const CabinetSpecRowSchema = z.object({
  cabinetId:               z.string().uuid(),
  cabinetName:             z.string(),
  dominantSpecialisation:  z.string().nullable(),
  actesCount:              z.number().int().nonnegative(),
  llmCostUsd:              z.number().nonnegative(),
});
export type CabinetSpecRow = z.infer<typeof CabinetSpecRowSchema>;

/**
 * Réponse complète de GET /api/admin/stats/product-usage
 */
export const ProductUsageResponseSchema = z.object({
  kpis:                          ProductUsageKpisSchema,
  specialisationActTypeFlows:    z.array(SankeyFlowSchema),
  cabinetsBySpecialisation:      z.array(CabinetSpecRowSchema),
});
export type ProductUsageResponse = z.infer<typeof ProductUsageResponseSchema>;

/**
 * Réponse all-time volontairement séparée de la lecture initiale de la page.
 * Son histogramme scanne l'ensemble des dossiers : le front ne l'active qu'à
 * l'ouverture explicite du panneau, puis peut le garder plus longtemps en cache.
 */
export const CaseSizeDistributionResponseSchema = z.object({
  caseSizeDistribution: z.array(HistogramBucketSchema),
  avgCaseSizePieces: z.number().nonnegative(),
});
export type CaseSizeDistributionResponse = z.infer<typeof CaseSizeDistributionResponseSchema>;

/**
 * Une ligne de la table "Activité dossiers" (drilldowns cabinet/user).
 */
export const CaseActivityRowSchema = z.object({
  caseId:                  z.string().uuid(),
  caseName:                z.string(),
  pieceCount:              z.number().int().nonnegative(),
  hasSummary:              z.boolean(),
  actesGenerated:          z.number().int().nonnegative(),
  caseDiscoMessages:       z.number().int().nonnegative(),
  caseDiscoConversations:  z.number().int().nonnegative(),
  llmCostUsd:              z.number().nonnegative(),
});
export type CaseActivityRow = z.infer<typeof CaseActivityRowSchema>;

/**
 * Réponse de GET /api/admin/stats/cases-by-cabinet/:id et /cases-by-user/:id
 */
export const CasesActivityResponseSchema = z.object({
  cases: z.array(CaseActivityRowSchema),
});
export type CasesActivityResponse = z.infer<typeof CasesActivityResponseSchema>;

// ============================================================
// Engagement conversations (Case Discovery)
// ============================================================

/**
 * KPIs d'engagement basés sur casediscovery_conversations / casediscovery_messages.
 * Tous filtrés par période sauf `pctCasesWithConversation` qui rapporte aux dossiers
 * tout court (cf. README de la section dans le dashboard).
 */
export const EngagementKpisSchema = z.object({
  avgConversationsPerCase:       z.number().nonnegative(),
  medianMessagesPerConversation: z.number().nonnegative(),
  pctCasesWithConversation:      z.number().min(0).max(1),
  userMessages:                  z.number().int().nonnegative(),
  assistantMessages:             z.number().int().nonnegative(),
});
export type EngagementKpis = z.infer<typeof EngagementKpisSchema>;

/**
 * Réponse complète de GET /api/admin/stats/engagement
 */
export const EngagementResponseSchema = z.object({
  kpis:                            EngagementKpisSchema,
  messagesPerConversationDistrib:  z.array(HistogramBucketSchema),
  usersByActivityDistrib:          z.array(HistogramBucketSchema),
});
export type EngagementResponse = z.infer<typeof EngagementResponseSchema>;
